import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/hooks/useI18n';
import { IPaymentsPage } from '@/lib/payments/models/payments-dashboard.model';

interface PaymentsPaginationProps {
    page: IPaymentsPage;
    onPageChange: (page: number) => void;
    /** Disattiva i pulsanti mentre la pagina richiesta sta arrivando. */
    isLoading?: boolean;
}

/**
 * Pagine numerate, non scroll infinito: le rate sono una coda che si smaltisce e ogni
 * incasso invalida la query. Uno scroll infinito ricaricato a meta' cambierebbe le righe
 * sotto le dita, saltandone alcune senza che si veda.
 *
 * Il conteggio mostrato e' il totale del filtro, che e' anche l'insieme su cui sono
 * calcolati i KPI: cosi' si legge subito che "3 di 47" non contraddice il totale in alto.
 */
export const PaymentsPagination = ({ page, onPageChange, isLoading }: PaymentsPaginationProps) => {
    const { t } = useI18n();
    const pageCount = Math.max(1, Math.ceil(page.total_count / page.page_size));

    if (page.total_count <= page.page_size) return null;

    const from = (page.page - 1) * page.page_size + 1;
    const to = Math.min(page.page * page.page_size, page.total_count);

    return (
        <div className="flex items-center justify-between gap-2 pt-3">
            <p className="text-xs text-gray-500">
                {t('payments.pagination.range', {
                    from,
                    to,
                    total: page.total_count,
                })}
            </p>

            <div className="flex items-center gap-1">
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={isLoading || page.page <= 1}
                    onClick={() => onPageChange(page.page - 1)}
                    aria-label={t('common.previous')}
                >
                    <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-2 text-xs text-gray-600">
                    {t('payments.pagination.page', { page: page.page, pageCount })}
                </span>
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={isLoading || page.page >= pageCount}
                    onClick={() => onPageChange(page.page + 1)}
                    aria-label={t('common.next')}
                >
                    <ChevronRight className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );
};
